/**
 * Tradovate WebSocket Wire Format
 *
 * Parser and formatter for Tradovate's custom WebSocket protocol.
 *
 * Frame Protocol:
 * - "o" - Connection opened
 * - "h" - Heartbeat
 * - "c" - Connection closed
 * - "a[...]" - Data frame (JSON array of messages)
 *
 * Message Types:
 * - Response: {"i": request_id, "s": status, "d": data}
 * - Event: {"e": event_type, "d": data}
 *
 * Ported from: wire_format.rs (412 lines)
 */

import type { WebSocketMessage, WebSocketMessageData, RepositoryEvent } from './types';

/**
 * Wire Format Parser and Formatter
 *
 * Static utility class for Tradovate WebSocket protocol.
 */
export class WireFormat {
  /**
   * Format a request for WebSocket transmission
   *
   * Format: {endpoint}\n{request_id}\n{query_params}\n{body}
   *
   * For GET requests:
   *   - Query params go on line 3 as "key1=val1&key2=val2"
   *   - Body (line 4) is empty
   *
   * For POST/PUT/DELETE:
   *   - Line 3 is empty
   *   - Body goes on line 4 as JSON
   *
   * Rust equivalent:
   * ```rust
   * pub fn format_request(endpoint: &str, request_id: u64, body: &Value) -> String {
   *   let endpoint = endpoint.strip_prefix('/').unwrap_or(endpoint);
   *   let body_str = serde_json::to_string(body).unwrap_or_else(|_| "{}".to_string());
   *   format!("{}\n{}\n\n{}", endpoint, request_id, body_str)
   * }
   * ```
   *
   * @example
   * ```typescript
   * // POST request
   * const msg = WireFormat.formatRequest('md/subscribeQuote', 42, { symbol: 'ESH5' }, 'POST');
   * // Returns: "md/subscribeQuote\n42\n\n{\"symbol\":\"ESH5\"}"
   *
   * // GET request
   * const msg = WireFormat.formatRequest('userAchievement/deps', 10, { masterid: 58 }, 'GET');
   * // Returns: "userAchievement/deps\n10\nmasterid=58\n"
   * ```
   */
  static formatRequest(
    endpoint: string,
    requestId: number,
    paramsOrBody?: any,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'POST'
  ): string {
    // Remove leading "/" if present
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

    if (method === 'GET' && paramsOrBody && typeof paramsOrBody === 'object') {
      // GET: Format as query string on line 3
      const queryString = new URLSearchParams(
        Object.entries(paramsOrBody).map(([k, v]) => [k, String(v)])
      ).toString();

      // Format: endpoint\nrequestId\nqueryString\n
      return `${cleanEndpoint}\n${requestId}\n${queryString}\n`;
    } else {
      // POST/PUT/DELETE: JSON body on line 4
      const bodyStr = paramsOrBody ? JSON.stringify(paramsOrBody) : '{}';

      // Format: endpoint\nrequestId\n\nbody
      return `${cleanEndpoint}\n${requestId}\n\n${bodyStr}`;
    }
  }

  /**
   * Format authorization message
   *
   * Format: authorize\n0\n\n{token}
   *
   * @param token - Access token
   */
  static formatAuthorization(token: string): string {
    return `authorize\n0\n\n${token}`;
  }

  /**
   * Format heartbeat message
   *
   * Returns: "[]"
   */
  static formatHeartbeat(): string {
    return '[]';
  }

  /**
   * Parse WebSocket frame into structured message
   *
   * Rust equivalent:
   * ```rust
   * pub fn parse_websocket_message(text: &str) -> Option<WebSocketMessage> {
   *   match text.chars().next() {
   *     Some('a') => { parse array... }
   *     Some('o') => Some(WebSocketMessage::Open),
   *     Some('h') => Some(WebSocketMessage::Heartbeat),
   *     Some('c') => Some(WebSocketMessage::Close),
   *     _ => None
   *   }
   * }
   * ```
   *
   * @param text - Raw WebSocket frame text
   * @returns Parsed message or null
   */
  static parseFrame(text: string): WebSocketMessage | null {
    if (!text || text.length === 0) {
      return null;
    }

    const firstChar = text[0];

    switch (firstChar) {
      case 'a': {
        // Array frame: "a[{...}, {...}]"
        try {
          const jsonArray = JSON.parse(text.slice(1));  // Parse everything after 'a'

          if (!Array.isArray(jsonArray)) {
            console.warn('Expected array after "a" frame, got:', typeof jsonArray);
            return null;
          }

          const messages: WebSocketMessageData[] = [];

          for (const jsonMsg of jsonArray) {
            const parsed = this.parseMessageData(jsonMsg);
            if (parsed) {
              messages.push(parsed);
            }
          }

          return { type: 'data', messages };

        } catch (error) {
          console.error('Failed to parse array frame:', error);
          return null;
        }
      }

      case 'o':
        return { type: 'open' };

      case 'h':
        return { type: 'heartbeat' };

      case 'c':
        return { type: 'close' };

      default: {
        // Try parsing as direct JSON message (fallback)
        try {
          const jsonMsg = JSON.parse(text);
          const parsed = this.parseMessageData(jsonMsg);

          if (parsed) {
            return { type: 'data', messages: [parsed] };
          }
        } catch (error) {
          console.error('Failed to parse WebSocket frame:', text, error);
        }

        return null;
      }
    }
  }

  /**
   * Parse individual message data from JSON
   *
   * Determines message type (response, event, market data, etc.)
   *
   * Rust equivalent:
   * ```rust
   * pub fn parse_message_data(json_msg: &Value) -> Option<WebSocketMessageData> {
   *   if let Some(request_id) = json_msg.get("i").and_then(|v| v.as_u64()) {
   *     // Response message
   *     return Some(WebSocketMessageData::Response { ... });
   *   }
   *   if let Some(event_type) = json_msg.get("e").and_then(|v| v.as_str()) {
   *     // Event message
   *     match event_type {
   *       "md" => ...
   *       "chart" => ...
   *       "props" => ...
   *     }
   *   }
   * }
   * ```
   */
  static parseMessageData(jsonMsg: any): WebSocketMessageData | null {
    if (!jsonMsg || typeof jsonMsg !== 'object') {
      return null;
    }

    // Check if it's a response message (has "i" field)
    if (typeof jsonMsg.i === 'number') {
      return {
        type: 'response',
        requestId: jsonMsg.i,
        status: typeof jsonMsg.s === 'number' ? jsonMsg.s : 500,
        data: jsonMsg.d ?? null
      };
    }

    // Check if it's an event message (has "e" field)
    if (typeof jsonMsg.e === 'string') {
      const eventType = jsonMsg.e;
      const data = jsonMsg.d ?? {};

      switch (eventType) {
        case 'md': {
          // Market data event
          return {
            type: 'md',
            quotes: data.q ?? undefined,          // "q" field - quote updates
            dom: data.d ?? undefined,             // "d" field - depth of market
            histograms: data.histograms ?? undefined
          };
        }

        case 'chart': {
          // Chart data event
          return {
            type: 'chart',
            charts: data.charts ?? []
          };
        }

        case 'shutdown': {
          return {
            type: 'shutdown',
            reasonCode: data.reasonCode,
            reason: data.reason,
          }
        }

        case 'clock': {
          // Pass through clock data for Market Replay synchronization
          return {
            type: 'event',
            eventType: 'clock',
            data,
          }
        }

        case 'props': {
          // Property/entity update event (strict per API):
          // d: { entityType, eventType, id?, entity: T | T[] }
          try {
            const repoEvent: RepositoryEvent = {
              entityType: data.entityType ?? '',
              eventType: data.eventType ?? '',
              id: data.id,
              d: data.entity,
            };

            return { type: 'props', repositoryEvent: repoEvent };
          } catch (error) {
            console.error('Failed to parse props event:', error);
            return { type: 'unknown', eventType, data };
          }
        }

        case 'sentiment': {
          // Sentiment event: { e: 'sentiment', d: { g: 'ES', f: { sentiment, shortAvgPrice, longAvgPrice } } }
          return {
            type: 'sentiment',
            data  // Pass through entire data object
          };
        }

        default:
          // Unknown event type
          return {
            type: 'unknown',
            eventType,
            data
          };
      }
    }

    // Unknown message format
    return {
      type: 'unknown',
      data: jsonMsg
    };
  }

  /**
   * Check if message is a response (has "i" field)
   */
  static isResponse(message: any): boolean {
    return message && typeof message.i === 'number';
  }

  /**
   * Check if message is an event (has "e" field)
   */
  static isEvent(message: any): boolean {
    return message && typeof message.e === 'string';
  }

  /**
   * Extract request ID from response message
   */
  static extractRequestId(message: any): number | null {
    return typeof message?.i === 'number' ? message.i : null;
  }

  /**
   * Extract status code from response
   */
  static extractStatus(message: any): number {
    return typeof message?.s === 'number' ? message.s : 500;
  }

  /**
   * Extract response data
   */
  static extractResponseData(message: any): any {
    return message?.d ?? null;
  }

  /**
   * Extract event type from event message
   */
  static extractEventType(message: any): string | null {
    return typeof message?.e === 'string' ? message.e : null;
  }

  /**
   * Extract event data
   */
  static extractEventData(message: any): any {
    return message?.d ?? null;
  }
}
